<template>
  <div class="ftc-root">
    <div class="ftc-bar">
      <!-- legend is always present for 2+ series (identity is never
           color-alone); a single series needs no box, the card title
           already names it -->
      <span v-if="series.length > 1 || avgWindow" class="ftc-legend">
        <span v-for="s in series" :key="'lg-' + s.label" class="ftc-legend-item">
          <span class="ftc-swatch" :style="{ background: s.color }"></span>{{ s.label }}
        </span>
        <span v-if="avgWindow" class="ftc-legend-item"><span class="ftc-linekey"></span>{{ avgLabel }}</span>
      </span>
      <span class="ftc-toggle" role="group" aria-label="View">
        <button type="button" class="ftc-chip" :class="{ 'ftc-chip--active': view === 'chart' }"
                :aria-pressed="view === 'chart' ? 'true' : 'false'" @click="view = 'chart'">Chart</button>
        <button type="button" class="ftc-chip" :class="{ 'ftc-chip--active': view === 'table' }"
                :aria-pressed="view === 'table' ? 'true' : 'false'" @click="view = 'table'">Table</button>
      </span>
    </div>

    <!-- role=group, not img: img flattens descendants and a screen reader
         could never hear the values; the live region voices each step -->
    <div
      v-if="view === 'chart'"
      ref="wrap"
      class="ftc-wrap"
      tabindex="0"
      role="group"
      :aria-label="ariaLabel"
      @keydown.left.prevent="nudgeHover(-1)"
      @keydown.right.prevent="nudgeHover(1)"
      @keydown.esc="hoverIdx = null"
      @blur="hoverIdx = null"
      @mouseleave="hoverIdx = null"
    >
      <span class="sr-only" aria-live="polite">{{ tipText }}</span>
      <svg :width="chartW" :height="height">
        <g>
          <line v-for="t in yTicks" :key="'g' + t.v" class="ftc-grid"
                :x1="margins.l" :x2="chartW - margins.r" :y1="t.y" :y2="t.y" />
          <text v-for="t in yTicks" :key="'y' + t.v" class="ftc-tick"
                :x="margins.l - 8" :y="t.y + 3.5" text-anchor="end">{{ t.v.toLocaleString() }}</text>
        </g>
        <g v-for="(b, i) in buckets" :key="'b' + i" :class="{ 'ftc-lift': hoverIdx === i }">
          <path v-for="seg in b.segs" :key="seg.si" :d="seg.d" :style="{ fill: seg.color }" />
        </g>
        <path v-if="avgPath" :d="avgPath" class="ftc-avg" />
        <g>
          <text v-for="b in xLabels" :key="'x' + b.i" class="ftc-tick"
                :x="b.x" :y="height - 8" text-anchor="middle">{{ b.label }}</text>
        </g>
        <line v-if="hoverIdx !== null && buckets[hoverIdx]" class="ftc-cross"
              :x1="buckets[hoverIdx].cx" :x2="buckets[hoverIdx].cx"
              :y1="margins.t" :y2="height - margins.b" />
        <!-- hit targets: the whole column slot, never just the painted bar -->
        <rect v-for="(b, i) in buckets" :key="'h' + i" class="ftc-hit"
              :x="b.x0" :y="margins.t" :width="b.slotW" :height="height - margins.t - margins.b"
              @mouseenter="hoverIdx = i" />
      </svg>
      <div v-if="tipModel" class="ftc-tip" :style="tipStyle">
        <div class="ftc-tip-title">{{ tipModel.title }}</div>
        <div v-for="(row, ri) in tipModel.rows" :key="'tr' + ri" class="ftc-tip-row"
             :class="{ 'ftc-tip-row--rule': row.rule }">
          <span class="ftc-tip-key" :style="row.color ? { background: row.color } : null"></span>
          <span class="ftc-tip-val">{{ row.v }}</span>
          <span class="ftc-tip-lbl">{{ row.label }}</span>
        </div>
      </div>
    </div>

    <!-- tabindex: safari does not make overflow scrollers keyboard
         focusable on its own; this table is the chart's accessible twin -->
    <div v-else class="ftc-tv-scroll" tabindex="0" role="region" :aria-label="ariaLabel + ', table view'">
      <table class="ftc-tv">
        <thead>
          <tr>
            <th>{{ tableLabel }}</th>
            <th v-for="s in series" :key="'th-' + s.label">{{ s.label }}</th>
            <th v-if="series.length > 1">Total</th>
            <th v-if="avgWindow">{{ avgLabel }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(r, i) in rows" :key="'tv' + i">
            <td>{{ r.label }}</td>
            <td v-for="(s, si) in series" :key="'td' + si">{{ (r.values || [])[si] || 0 }}</td>
            <td v-if="series.length > 1">{{ totals[i] }}</td>
            <td v-if="avgWindow">{{ avgSeries[i] }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
// shared svg stacked-column timeline, extracted from the orm analytics
// hero's mark language so every dashboard's time chart carries the same
// specs: thin columns with 2px surface gaps and rounded data-ends, solid
// hairline gridlines, a crosshair tooltip with values leading, keyboard
// stepping voiced through a live region, and a table twin. series colors
// arrive as props (already palette-validated by the host per theme); all
// chart furniture inks come from theme tokens in the css below.
export default {
  name: 'fi_timeline_chart',

  props: {
    // [{ label: 'Jul 3', values: [3, 1] }] -- values align to `series`
    rows: { type: Array, required: true },
    // [{ label: 'Issues', color: '#d9730d' }]
    series: { type: Array, required: true },
    height: { type: Number, default: 300 },
    // 0 disables the trailing-average line
    avgWindow: { type: Number, default: 0 },
    avgLabel: { type: String, default: 'avg' },
    tableLabel: { type: String, default: 'Day' },
    chartLabel: { type: String, default: 'Chart' },
  },

  data() {
    return {
      view: 'chart',
      hoverIdx: null,
      chartW: 640,
    }
  },

  computed: {
    margins() {
      return { l: 46, r: 14, t: 12, b: 26 }
    },
    ariaLabel() {
      return this.chartLabel + '. Use arrow keys to step through values, or switch to the table view.'
    },
    totals() {
      return this.rows.map(r => (r.values || []).reduce((a, b) => a + (b || 0), 0))
    },
    avgSeries() {
      if (!this.avgWindow) return []
      const t = this.totals
      return t.map((_, i) => {
        const from = Math.max(0, i - this.avgWindow + 1)
        const slice = t.slice(from, i + 1)
        return Math.round((slice.reduce((a, b) => a + b, 0) / slice.length) * 10) / 10
      })
    },
    yMax() {
      const raw = Math.max(1, ...this.totals)
      // counts are integers, so the axis never steps finer than 1
      const step = Math.max(1, this.niceStep(raw / 4))
      return { step, max: Math.ceil(raw / step) * step }
    },
    yTicks() {
      const { step, max } = this.yMax
      const ticks = []
      for (let v = 0; v <= max; v += step) ticks.push({ v, y: this.yFor(v) })
      return ticks
    },
    buckets() {
      const m = this.margins
      const n = this.rows.length
      if (!n) return []
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const slotW = plotW / n
      const barW = Math.max(2, Math.min(24, slotW - Math.max(2, slotW * 0.25)))
      const baseY = this.height - m.b
      return this.rows.map((r, i) => {
        const x0 = m.l + slotW * i
        const x = x0 + (slotW - barW) / 2
        const order = this.series
          .map((s, si) => ({ si, color: s.color, v: (r.values || [])[si] || 0 }))
          .filter(s => s.v > 0)
        const topIdx = order.length - 1
        let bottom = baseY
        const segs = order.map((s, oi) => {
          const h = baseY - this.yFor(s.v)
          const yTop = bottom - h
          // 2px surface gap between touching segments (never a stroke); the
          // gap comes out of the upper segment so the baseline stays square
          const gap = oi > 0 ? 2 : 0
          const drawH = Math.max(h - gap, h > 0 ? 1 : 0)
          const drawBottom = bottom - gap
          const d = oi === topIdx
            ? this.roundedTopPath(x, drawBottom - drawH, barW, drawH)
            : 'M' + x + ',' + (drawBottom - drawH) + 'h' + barW + 'v' + drawH + 'h-' + barW + 'Z'
          bottom = yTop
          return { si: s.si, color: s.color, d }
        })
        return { x0, slotW, cx: x0 + slotW / 2, segs }
      })
    },
    avgPath() {
      if (!this.avgWindow || this.buckets.length < 2) return ''
      return this.buckets
        .map((b, i) => (i ? 'L' : 'M') + b.cx.toFixed(1) + ',' + this.yFor(this.avgSeries[i]).toFixed(1))
        .join(' ')
    },
    hTickEvery() {
      // thin by available width, not just bucket count: a date label needs
      // ~44px of slot or phone widths collide
      const m = this.margins
      const plotW = Math.max(50, this.chartW - m.l - m.r)
      const maxLabels = Math.max(3, Math.min(12, Math.floor(plotW / 44)))
      return Math.max(1, Math.ceil(this.rows.length / maxLabels))
    },
    xLabels() {
      return this.buckets
        .map((b, i) => ({ i, x: b.cx, label: (this.rows[i] || {}).label }))
        .filter(b => b.i % this.hTickEvery === 0)
    },
    tipModel() {
      const i = this.hoverIdx
      const r = i !== null ? this.rows[i] : null
      if (!r) return null
      const rows = this.series.map((s, si) => ({ color: s.color, label: s.label, v: (r.values || [])[si] || 0 }))
      if (this.series.length > 1) rows.push({ label: 'Total', v: this.totals[i], rule: true })
      if (this.avgWindow) rows.push({ color: 'var(--fi-text, #33414f)', label: this.avgLabel, v: this.avgSeries[i] })
      return { title: r.label, rows }
    },
    // spoken twin of the tooltip for the aria-live region
    tipText() {
      const t = this.tipModel
      if (!t) return ''
      return t.title + ': ' + t.rows.map(r => r.v + ' ' + r.label).join(', ')
    },
    tipStyle() {
      const b = this.hoverIdx !== null ? this.buckets[this.hoverIdx] : null
      if (!b) return {}
      // flip on remaining space, not the midpoint: at narrow widths (or
      // with long series labels) a midpoint flip lets the tip cross the
      // chart edge and get clipped by overflow-hidden card ancestors.
      // css bounds the width (max-width + wrapping labels); this bounds
      // the position on both sides.
      const flip = b.cx + 12 + 180 > this.chartW
      return flip
        ? { left: Math.max(4, b.cx - 12) + 'px', transform: 'translateX(-100%)' }
        : { left: (b.cx + 12) + 'px' }
    },
  },

  watch: {
    view() { this.$nextTick(() => this.measure()) },
    rows() { this.hoverIdx = null },
  },

  mounted() {
    this.measure()
    if (typeof ResizeObserver !== 'undefined') {
      this._ro = new ResizeObserver(() => this.measure())
      this._ro.observe(this.$el)
    } else {
      this._onResize = () => this.measure()
      window.addEventListener('resize', this._onResize)
    }
  },

  updated() {
    this.measure()
  },

  beforeDestroy() {
    if (this._ro) this._ro.disconnect()
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },

  methods: {
    measure() {
      // guard against the updated() loop: only assign on a real change
      const el = this.$refs.wrap || this.$el
      if (el && el.clientWidth && el.clientWidth !== this.chartW) this.chartW = el.clientWidth
    },
    niceStep(raw) {
      // snap an axis step to 1/2/5 x 10^k so tick values read clean
      const mag = Math.pow(10, Math.floor(Math.log10(Math.max(raw, 0.1))))
      for (const m of [1, 2, 5, 10]) {
        if (raw <= m * mag) return m * mag
      }
      return 10 * mag
    },
    yFor(v) {
      const m = this.margins
      const plotH = this.height - m.t - m.b
      return this.height - m.b - (v / this.yMax.max) * plotH
    },
    roundedTopPath(x, y, w, h, r) {
      // rounded data-end (top corners only); the baseline end stays square
      const rr = Math.min(r || 3, w / 2, h)
      if (rr <= 0.5) return 'M' + x + ',' + y + 'h' + w + 'v' + h + 'h-' + w + 'Z'
      return 'M' + x + ',' + (y + h) +
        'L' + x + ',' + (y + rr) +
        'Q' + x + ',' + y + ' ' + (x + rr) + ',' + y +
        'L' + (x + w - rr) + ',' + y +
        'Q' + (x + w) + ',' + y + ' ' + (x + w) + ',' + (y + rr) +
        'L' + (x + w) + ',' + (y + h) + 'Z'
    },
    nudgeHover(step) {
      const n = this.buckets.length
      if (!n) return
      const cur = this.hoverIdx === null ? (step > 0 ? -1 : n) : this.hoverIdx
      this.hoverIdx = Math.max(0, Math.min(n - 1, cur + step))
    },
  },
}
</script>

<style scoped>
.ftc-root {
  /* the bars and swatches are the data: without this, page print
     stylesheets strip the backgrounds to white */
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

.ftc-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.ftc-legend {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.ftc-legend-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #6b7a8c);
}
.ftc-swatch {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  flex: none;
}
.ftc-linekey {
  display: inline-block;
  width: 14px;
  height: 2px;
  border-radius: 2px;
  background: var(--fi-text, #33414f);
}
.ftc-toggle { display: inline-flex; gap: 4px; margin-left: auto; }
.ftc-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: 999px;
  padding: 2px 9px;
  font-size: 11.5px;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.ftc-chip--active {
  background: var(--fi-ops-accent, #2b6cb8);
  border-color: var(--fi-ops-accent, #2b6cb8);
  color: #fff;
}
.ftc-chip:focus-visible { outline: 2px solid var(--fi-ops-accent, #2b6cb8); outline-offset: 1px; }

.ftc-wrap {
  position: relative;
  outline: none;
}
.ftc-wrap:focus-visible {
  border-radius: 8px;
  box-shadow: 0 0 0 2px var(--fi-ops-accent, #2b6cb8);
}
.ftc-grid { stroke: var(--fi-line, #e9eef3); stroke-width: 1; }
.ftc-tick {
  fill: var(--fi-text-muted, #6b7a8c);
  font-size: 10.5px;
  font-variant-numeric: tabular-nums;
}
.ftc-lift path { filter: brightness(1.12); }
.ftc-avg {
  fill: none;
  stroke: var(--fi-text, #33414f);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}
.ftc-cross {
  stroke: color-mix(in srgb, var(--fi-text-muted, #6b7a8c) 55%, transparent);
  stroke-width: 1;
}
.ftc-hit { fill: transparent; }

.ftc-tip {
  position: absolute;
  top: 10px;
  z-index: 5;
  min-width: 150px;
  max-width: min(320px, calc(100% - 8px));
  padding: 8px 12px 9px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: 10px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.18);
  pointer-events: none;
}
.ftc-tip-title {
  font-size: 11px;
  font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  margin-bottom: 4px;
}
.ftc-tip-row {
  display: grid;
  grid-template-columns: 14px 40px 1fr;
  gap: 6px;
  align-items: center;
  font-size: 12.5px;
  line-height: 1.55;
}
.ftc-tip-row--rule {
  border-top: 1px solid var(--fi-line, #e3e9ef);
  margin-top: 3px;
  padding-top: 3px;
}
/* value leads, label follows: here the reader has the series, wants the number */
.ftc-tip-val {
  font-weight: 700;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--fi-text, #2c3a49);
}
/* labels wrap rather than push the tip past its max-width: failure-reason
   series carry raw carrier response text that can run 40+ characters */
.ftc-tip-lbl { color: var(--fi-text-muted, #6b7a8c); overflow-wrap: anywhere; }
.ftc-tip-key { height: 3px; border-radius: 2px; }

.ftc-tv-scroll { max-height: 300px; overflow: auto; }
/* separate + box-shadow, not collapse + border: collapsed borders detach
   from a position:sticky th and scroll away with the body */
.ftc-tv { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 12.5px; }
.ftc-tv th {
  position: sticky;
  top: 0;
  background: var(--fi-surface, #fff);
  text-align: right;
  font-weight: 700;
  color: var(--fi-text-muted, #6b7a8c);
  padding: 4px 10px;
  box-shadow: 0 1px 0 var(--fi-line, #d6dde6);
}
.ftc-tv th:first-child { text-align: left; }
.ftc-tv td {
  text-align: right;
  font-variant-numeric: tabular-nums;
  padding: 3px 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--fi-line, #e3e9ef) 55%, transparent);
  color: var(--fi-text, #2c3a49);
}
.ftc-tv td:first-child { text-align: left; color: var(--fi-text-muted, #6b7a8c); }
</style>
